// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { isRead } from '@polarissleep/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  countUnreadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  searchNotifications,
} from './notification-query';

/** Polling interval for the unread badge. 60s is responsive enough without hammering Medplum. */
const POLL_INTERVAL_MS = 60_000;

/** How many notifications we cache in memory at once. */
const PAGE_SIZE = 20;

export interface UseNotificationsResult {
  notifications: Communication[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  markRead: (communication: Communication) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * Hook that fetches the recipient's in-app notifications and unread count with a
 * 60s polling refresh. Polling pauses when the document is hidden. Pass undefined
 * for `recipientRef` while the profile is still loading; the hook will no-op.
 */
export function useNotificationsForRecipient(recipientRef: string | undefined): UseNotificationsResult {
  const medplum = useMedplum();

  const [notifications, setNotifications] = useState<Communication[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const recipientRefRef = useRef(recipientRef);
  useEffect(() => {
    recipientRefRef.current = recipientRef;
  }, [recipientRef]);

  const refresh = useCallback(async (): Promise<void> => {
    const ref = recipientRefRef.current;
    if (!ref) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [list, unread] = await Promise.all([
        searchNotifications(medplum, ref, { count: PAGE_SIZE }),
        countUnreadNotifications(medplum, ref),
      ]);
      setNotifications(list);
      setUnreadCount(unread);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    if (recipientRef) {
      refresh().catch(() => undefined);
    }
  }, [recipientRef, refresh]);

  useEffect(() => {
    if (!recipientRef) {
      return;
    }
    const tick = (): void => {
      if (document.visibilityState === 'visible') {
        refresh().catch(() => undefined);
      }
    };
    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [recipientRef, refresh]);

  const markRead = useCallback(
    async (communication: Communication): Promise<void> => {
      if (isRead(communication)) {
        return;
      }
      const updated = await markNotificationRead(medplum, communication);
      setNotifications((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    },
    [medplum]
  );

  const markAllRead = useCallback(async (): Promise<void> => {
    if (!recipientRefRef.current) {
      return;
    }
    await markAllNotificationsRead(medplum, recipientRefRef.current);
    await refresh();
  }, [medplum, refresh]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
  };
}
